import type { UsageReport, UsageRow, UsageTotals } from '@deskworks/shared-types';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { BarChart3, Coins, CreditCard, Gauge, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Page, PageHeader } from '@/components/page';
import { Section } from '@/components/section';
import { EmptyState, ErrorState, LoadingPage } from '@/components/states';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UsageChart, type UsageMetric } from '@/components/usage-chart';
import { api } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { intlLocale, t } from '@/lib/i18n';
import { RUNTIME_TYPE } from '@/lib/labels';
import { qk } from '@/lib/query-keys';
import { formatTokens, formatUsd, totalTokens, totalUsd } from '@/lib/usage';
import type { UsageRange } from '@/router';

const RANGES: Record<UsageRange, string> = {
  '7d': t('Last 7 days'),
  '30d': t('Last 30 days'),
  '90d': t('Last 90 days'),
  month: t('This month'),
  'last-month': t('Last month'),
};

const day = (d: Date) => d.toISOString().slice(0, 10);

/** Inclusive UTC days for a range. */
function rangeDays(range: UsageRange): { from: string; to: string } {
  const now = new Date();
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const back = (n: number) => new Date(today.getTime() - n * 86_400_000);
  switch (range) {
    case '7d':
      return { from: day(back(6)), to: day(today) };
    case '90d':
      return { from: day(back(89)), to: day(today) };
    case 'month':
      return { from: day(new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1))), to: day(today) };
    case 'last-month':
      return {
        from: day(new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - 1, 1))),
        to: day(new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 0))),
      };
    default:
      return { from: day(back(29)), to: day(today) };
  }
}

export function UsagePage() {
  const workspaceId = useWorkspaceId();
  const search = useSearch({ from: '/_authed/w/$workspaceId/usage' });
  const navigate = useNavigate({ from: '/w/$workspaceId/usage' });
  const range = search.range ?? '30d';
  const metric: UsageMetric = search.metric ?? 'cost';
  const { from, to } = rangeDays(range);
  const report = useQuery({
    queryKey: qk.usage(workspaceId, from, to),
    queryFn: () => api.workspaces.usage(workspaceId, { from, to }),
    placeholderData: keepPreviousData,
  });

  if (report.isPending) return <LoadingPage />;

  return (
    <Page wide>
      <PageHeader
        title={t('AI usage')}
        description={t(
          'Tokens and money your agents used, by Desk, Agent and Work Item. Money is at the providers’ list prices: API runtimes are billed to your key, subscription runs are covered by your plan and shown as what they would have cost via the API.',
        )}
        actions={
          <Select value={range} onValueChange={(v) => void navigate({ search: (s) => ({ ...s, range: v as UsageRange }) })}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(RANGES) as UsageRange[]).map((r) => (
                <SelectItem key={r} value={r}>
                  {RANGES[r]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />
      {report.isError ? (
        <ErrorState error={report.error} onRetry={() => void report.refetch()} />
      ) : (
        <UsageBody
          report={report.data}
          workspaceId={workspaceId}
          metric={metric}
          onMetric={(m) => void navigate({ search: (s) => ({ ...s, metric: m }) })}
        />
      )}
    </Page>
  );
}

function UsageBody({
  report,
  workspaceId,
  metric,
  onMetric,
}: {
  report: UsageReport;
  workspaceId: string;
  metric: UsageMetric;
  onMetric: (m: UsageMetric) => void;
}) {
  const totals = report.totals;
  const cachedShare = totals.inputTokens ? Math.round((totals.cachedInputTokens / totals.inputTokens) * 100) : 0;
  const tiles: { label: string; value: string; hint: string; icon: LucideIcon }[] = [
    {
      label: t('Tokens'),
      value: formatTokens(totalTokens(totals)),
      hint: cachedShare
        ? t('{input} in ({share}% cached) · {output} out', { input: formatTokens(totals.inputTokens), share: cachedShare, output: formatTokens(totals.outputTokens) })
        : t('{input} in · {output} out', { input: formatTokens(totals.inputTokens), output: formatTokens(totals.outputTokens) }),
      icon: Gauge,
    },
    { label: t('API spend'), value: formatUsd(totals.apiCostUsd), hint: t('Billed to your API keys'), icon: CreditCard },
    { label: t('Subscription value'), value: formatUsd(totals.subscriptionValueUsd), hint: t('Covered by ChatGPT / Claude plans'), icon: Coins },
    {
      label: t('Agent runs'),
      value: totals.runs.toLocaleString(intlLocale),
      hint: totals.unpricedRuns ? t('{n} with a model of unknown price', { n: totals.unpricedRuns }) : t('Work Items, reviews, chats, Lead reviews'),
      icon: BarChart3,
    },
  ];

  if (totals.runs === 0) {
    return <EmptyState title={t('No agent runs in this period')} description={t('As soon as agents work, their tokens and costs show up here.')} />;
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {tiles.map((s) => (
          <Card key={s.label} className="p-4">
            <div className="flex items-center justify-between text-[13px] text-muted-foreground">
              <span>{s.label}</span>
              <s.icon className="size-4" />
            </div>
            <div className="mt-2 text-2xl font-semibold tabular-nums">{s.value}</div>
            <div className="mt-0.5 text-xs text-muted-foreground">{s.hint}</div>
          </Card>
        ))}
      </div>

      <Section
        title={t('Per day')}
        description={t('UTC days. Hover a bar for the numbers.')}
        actions={
          <Tabs value={metric} onValueChange={(v) => onMetric(v as UsageMetric)}>
            <TabsList>
              <TabsTrigger value="cost">{t('Cost')}</TabsTrigger>
              <TabsTrigger value="tokens">{t('Tokens')}</TabsTrigger>
            </TabsList>
          </Tabs>
        }
      >
        <UsageChart daily={report.daily} metric={metric} />
      </Section>

      <Tabs defaultValue="desks">
        <TabsList>
          <TabsTrigger value="desks">{t('By Desk')}</TabsTrigger>
          <TabsTrigger value="agents">{t('By Agent')}</TabsTrigger>
          <TabsTrigger value="items">{t('By Work Item')}</TabsTrigger>
          <TabsTrigger value="models">{t('By model')}</TabsTrigger>
          <TabsTrigger value="runtimes">{t('By runtime')}</TabsTrigger>
        </TabsList>
        <TabsContent value="desks">
          <UsageTable
            rows={report.byDesk}
            total={totals}
            nameHeader={t('Desk')}
            renderName={(r) =>
              r.id ? (
                <Link to="/w/$workspaceId/desks/$deskId" params={{ workspaceId, deskId: r.id }} className="hover:text-primary hover:underline">
                  {r.name}
                </Link>
              ) : (
                r.name
              )
            }
          />
        </TabsContent>
        <TabsContent value="agents">
          <UsageTable
            rows={report.byAgent}
            total={totals}
            nameHeader={t('Agent')}
            renderName={(r) =>
              r.id ? (
                <Link to="/w/$workspaceId/agents/$agentId" params={{ workspaceId, agentId: r.id }} className="hover:text-primary hover:underline">
                  {r.name}
                </Link>
              ) : (
                r.name
              )
            }
          />
        </TabsContent>
        <TabsContent value="items">
          <UsageTable
            rows={report.byWorkItem}
            total={totals}
            nameHeader={t('Work Item')}
            footnote={report.byWorkItem.length >= 100 ? t('The 100 most expensive.') : undefined}
            renderName={(r) => (
              <>
                {r.id ? (
                  <Link
                    to="/w/$workspaceId/work-items/$workItemId"
                    params={{ workspaceId, workItemId: r.id }}
                    className="hover:text-primary hover:underline"
                  >
                    {r.name}
                  </Link>
                ) : (
                  <span className="italic">{r.name}</span>
                )}
                {r.detail && <div className="text-xs text-muted-foreground">{r.detail}</div>}
              </>
            )}
          />
        </TabsContent>
        <TabsContent value="models">
          <UsageTable
            rows={report.byModel}
            total={totals}
            nameHeader={t('Model')}
            renderName={(r) => (
              <>
                <span className="font-mono text-[13px]">{r.name}</span>
                {r.detail && <div className="text-xs text-muted-foreground">{RUNTIME_TYPE[r.detail as keyof typeof RUNTIME_TYPE] ?? r.detail}</div>}
              </>
            )}
          />
        </TabsContent>
        <TabsContent value="runtimes">
          <UsageTable
            rows={report.byRuntime}
            total={totals}
            nameHeader={t('Runtime')}
            renderName={(r) => (
              <>
                {r.name}
                {r.detail && <div className="text-xs text-muted-foreground">{RUNTIME_TYPE[r.detail as keyof typeof RUNTIME_TYPE] ?? r.detail}</div>}
              </>
            )}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function UsageTable({
  rows,
  total,
  nameHeader,
  renderName,
  footnote,
}: {
  rows: UsageRow[];
  total: UsageTotals;
  nameHeader: string;
  renderName: (r: UsageRow) => ReactNode;
  footnote?: string;
}) {
  const totalMoney = totalUsd(total);
  const totalTok = totalTokens(total);
  return (
    <Card className="overflow-x-auto p-0">
      <table className="w-full min-w-[640px] text-sm">
        <thead>
          <tr className="border-b text-left text-xs text-muted-foreground">
            <th className="px-4 py-2.5 font-medium">{nameHeader}</th>
            <th className="px-3 py-2.5 text-right font-medium">{t('Runs')}</th>
            <th className="px-3 py-2.5 text-right font-medium">{t('Input')}</th>
            <th className="px-3 py-2.5 text-right font-medium">{t('Output')}</th>
            <th className="px-3 py-2.5 text-right font-medium">{t('API spend')}</th>
            <th className="px-3 py-2.5 text-right font-medium">{t('Subscription value')}</th>
            <th className="w-32 px-4 py-2.5 font-medium">{t('Share')}</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {rows.map((r, i) => {
            // Share of money where there is money, else of tokens.
            const share = totalMoney > 0 ? totalUsd(r) / totalMoney : totalTok > 0 ? totalTokens(r) / totalTok : 0;
            return (
              <tr key={`${r.id ?? 'none'}-${r.name}-${i}`} className="align-top">
                <td className="px-4 py-2.5">{renderName(r)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{r.runs.toLocaleString(intlLocale)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums" title={t('{n} ({cached} cached)', { n: r.inputTokens.toLocaleString(intlLocale), cached: r.cachedInputTokens.toLocaleString(intlLocale) })}>
                  {formatTokens(r.inputTokens)}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums" title={r.outputTokens.toLocaleString(intlLocale)}>
                  {formatTokens(r.outputTokens)}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">{r.apiCostUsd ? formatUsd(r.apiCostUsd) : <span className="text-muted-foreground">–</span>}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {r.subscriptionValueUsd ? formatUsd(r.subscriptionValueUsd) : <span className="text-muted-foreground">–</span>}
                  {r.unpricedRuns > 0 && <div className="text-xs text-muted-foreground">{t('{n} unpriced', { n: r.unpricedRuns })}</div>}
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                      <div className="h-full rounded-full bg-[#2a78d6] dark:bg-[#3987e5]" style={{ width: `${Math.round(share * 100)}%` }} />
                    </div>
                    <span className="w-9 text-right text-xs text-muted-foreground tabular-nums">{Math.round(share * 100)}%</span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {footnote && <p className="border-t px-4 py-2 text-xs text-muted-foreground">{footnote}</p>}
    </Card>
  );
}
